import React from "react";
import LoadingState from "../../components/basic/loading-state";
import Avatar from "../../components/basic/avatar";
import { resolveMediaUrl } from "../../helpers/media-url";
import type { AccountRow, Conversation } from "../../params/types";
import { formatRelative, igProfileUrl } from "../shared";
import { ChevronIcon, formatThreadPreview, IgProfileLink } from "./helpers";

export interface ThreadListProps {
  accounts: AccountRow[];
  selectedAccount: string;
  setSelectedAccount: (username: string) => void;
  conversations: Conversation[];
  selectedThreadId: string | null;
  selectedThreadIdRef: React.MutableRefObject<string | null>;
  setSelectedThreadId: (id: string | null) => void;
  setPromptDirty: (dirty: boolean) => void;
  setNewPeer: (peer: string) => void;
  markThreadRead: (account?: string | null, threadId?: string | null) => void;
  loadingConvos: boolean;
  hasMoreConvos?: boolean;
  loadingMore?: boolean;
  onLoadMore?: (() => void) | null;
  railExpanded: boolean;
  setRailExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  contactsRescanning?: boolean;
  onClearContacts?: (() => void) | null;
}

export function ThreadList({
  accounts,
  selectedAccount,
  setSelectedAccount,
  conversations,
  selectedThreadId,
  selectedThreadIdRef,
  setSelectedThreadId,
  setPromptDirty,
  setNewPeer,
  markThreadRead,
  loadingConvos,
  hasMoreConvos = false,
  loadingMore = false,
  onLoadMore = null,
  railExpanded,
  setRailExpanded,
  contactsRescanning = false,
  onClearContacts = null,
}: ThreadListProps) {
  return (
    <>
      <aside
        className={`ib-msg-accounts${railExpanded ? " ib-msg-accounts--expanded" : ""}`}
      >
        <button
          type="button"
          className="ib-msg-accounts__toggle"
          onClick={() => setRailExpanded((e) => !e)}
          title={railExpanded ? "Collapse" : "Expand"}
          aria-label={railExpanded ? "Collapse accounts" : "Expand accounts"}
        >
          <ChevronIcon expanded={railExpanded} />
        </button>
        {!accounts.length && <div className="ib-empty">No accounts</div>}
        {accounts.map((a) => {
          const unreadFromList =
            selectedAccount === a.username
              ? conversations.filter((c) => c.unread).length
              : null;
          const unreadCount =
            unreadFromList != null
              ? unreadFromList
              : Math.max(0, Number(a.unread_count) || 0);
          const igHandle = a.ig_username
            ? `@${String(a.ig_username).replace(/^@/, "")}`
            : "";
          return (
            <div
              key={a.username}
              className={`ib-msg-account-pill${
                selectedAccount === a.username
                  ? " ib-msg-account-pill--active"
                  : ""
              }`}
              role="button"
              tabIndex={0}
              aria-label={`Select account ${igHandle || a.username}`}
              title={igHandle ? `${igHandle} (${a.username})` : a.username}
              onClick={() => setSelectedAccount(a.username)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setSelectedAccount(a.username);
                }
              }}
            >
              <div className="ib-msg-account-pill__avatar">
                <Avatar
                  width={50}
                  height={50}
                  text={a.ig_username || a.username}
                  image={resolveMediaUrl(a.avatar_url) || null}
                  colorful={true}
                />
                {unreadCount > 0 && (
                  <span className="ib-msg-account-pill__badge">
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </div>
              {railExpanded && (
                <div className="ib-msg-account-pill__body">
                  <span className="ib-msg-account-pill__username">
                    {igHandle || a.username}
                  </span>
                  <span className="ib-msg-account-pill__name">
                    {igHandle
                      ? a.username
                      : a.proxy_label ||
                        `${a.conversation_count || 0} chats`}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </aside>

      <aside className="ib-msg-list ib-inbox-threads">
        <div className="ib-msg-list__header">
          {(() => {
            const sel = accounts.find((a) => a.username === selectedAccount);
            const ig = sel?.ig_username
              ? `@${String(sel.ig_username).replace(/^@/, "")}`
              : "";
            return (
              <>
                <span className="ib-msg-list__account-name">
                  {ig || selectedAccount || "Accounts"}
                </span>
                <IgProfileLink url={igProfileUrl(sel)} />
              </>
            );
          })()}
          <div className="ib-msg-list__header-row">
            <span className="ib-msg-list__count">
              {conversations.length}
              {hasMoreConvos ? "+" : ""} conversation
              {conversations.length !== 1 ? "s" : ""}
            </span>
            {onClearContacts && (
              <button
                type="button"
                className="ib-msg-list__clear"
                disabled={contactsRescanning || loadingConvos}
                title={
                  contactsRescanning
                    ? "Full rescan in progress"
                    : "Wipe contacts, messages, and followers, then rescan from Instagram"
                }
                onClick={() => onClearContacts()}
              >
                {contactsRescanning ? "Rescanning…" : "Clear"}
              </button>
            )}
          </div>
        </div>

        {loadingConvos && !conversations.length && (
          <LoadingState label="Loading conversations…" />
        )}
        {!loadingConvos && !conversations.length && (
          <div className="ib-empty">No conversations yet.</div>
        )}

        <div className="ib-msg-list__items ib-thread-list">
          {conversations.map((c) => {
            const peerLabel = c.peer_username || c.title || "user";
            return (
              <button
                key={c.thread_id}
                type="button"
                className={`ib-msg-item${
                  selectedThreadId === c.thread_id ? " ib-msg-item--active" : ""
                }${c.is_follower_only ? " ib-msg-item--follower" : ""}`}
                onClick={() => {
                  if (selectedThreadId === c.thread_id) return;
                  selectedThreadIdRef.current = c.thread_id;
                  setSelectedThreadId(c.thread_id);
                  setPromptDirty(false);
                  setNewPeer("");
                  if (c.unread) markThreadRead(selectedAccount, c.thread_id);
                }}
              >
                <Avatar
                  width={50}
                  height={50}
                  text={peerLabel}
                  colorful={true}
                  image={resolveMediaUrl(c.avatar_url) || null}
                />
                <span className="ib-msg-item__body">
                  <span className="ib-msg-item__top">
                    <span
                      className={`ib-msg-item__user${
                        c.unread ? " ib-msg-item__user--unread" : ""
                      }`}
                    >
                      {peerLabel}
                    </span>
                    <span className="ib-msg-item__time">
                      {c.is_follower_only
                        ? ""
                        : formatRelative(c.last_message_at || c.updated_at)}
                    </span>
                  </span>
                  <span className="ib-msg-item__preview">
                    {c.is_follower_only
                      ? "No messages yet"
                      : `${c.last_from === "me" ? "You: " : ""}${formatThreadPreview(c.last_preview)}`}
                  </span>
                </span>
                {c.unread && <span className="ib-msg-item__dot" />}
              </button>
            );
          })}
          {hasMoreConvos && (
            <button
              type="button"
              className="ib-msg-item ib-msg-item--more"
              onClick={() => onLoadMore?.()}
              disabled={loadingMore}
            >
              {loadingMore ? "Loading…" : "Load more"}
            </button>
          )}
        </div>
      </aside>
    </>
  );
}
